"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useGetNotifications,
  useGetUnreadNotificationCount,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotificationSignalR,
} from "@/lib/hooks/useNotification";
import type { AppNotification } from "@/lib/services/notificationService";
import contractService from "@/lib/services/contractService";
import { getContractViewHref } from "@/container/contracts/types/contract-type";

const CONTRACT_NOTIFICATION_TYPES = [3, 4, 5, 6, 7, 8, 9];

const formatDate = (value: string, locale: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale === "az" ? "az-AZ" : locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function NotificationBell() {
  const t = useTranslations("header.notifications");
  const locale = useLocale();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const { data: unreadCount = 0 } = useGetUnreadNotificationCount();
  const { data: notifications = [], isLoading } = useGetNotifications(open);
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  useNotificationSignalR(true, (notification) => {
    toast(notification.title, { description: notification.message });
  });

  const navigateToReference = async (notification: AppNotification) => {
    const contractId = Number(notification.referenceId);
    if (!CONTRACT_NOTIFICATION_TYPES.includes(notification.type) || !contractId) {
      return;
    }
    try {
      const res = await contractService.getContractById(contractId);
      const contract = res.responseValue;
      router.push(getContractViewHref(locale, contract ?? { id: contractId }));
    } catch {
      router.push(getContractViewHref(locale, { id: contractId }));
    }
  };

  const handleClick = (notification: AppNotification) => {
    setOpen(false);
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }
    void navigateToReference(notification);
  };

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("title")}
          className="relative p-2 rounded-full text-brand-muted-light hover:bg-brand-sand transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-brand-navy"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-error text-brand-cream text-[10px] font-semibold flex items-center justify-center">
              {badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-brand-cream text-brand-navy border-brand-dot-track">
        <div className="flex items-center justify-between px-4 py-3 border-b border-brand-dot-track">
          <span className="font-semibold text-brand-navy">{t("title")}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-xs text-brand-muted-light hover:bg-brand-sand hover:text-brand-navy"
            disabled={unreadCount === 0 || markAllAsRead.isPending}
            onClick={() => markAllAsRead.mutate()}
          >
            <CheckCheck className="h-4 w-4" />
            {t("readAll")}
          </Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-brand-placeholder" />
            </div>
          ) : notifications.length === 0 ? (
            <p className="py-8 text-center text-sm text-brand-muted-light">{t("empty")}</p>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => handleClick(notification)}
                className={cn(
                  "w-full text-left px-4 py-3 border-b border-brand-dot-track last:border-b-0 hover:bg-brand-sand transition-colors flex gap-3",
                  !notification.isRead && "bg-brand-sand"
                )}
              >
                <span
                  className={cn(
                    "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                    notification.isRead ? "bg-transparent" : "bg-brand-accent-text"
                  )}
                />
                <span className="flex-1 min-w-0">
                  <span
                    className={cn(
                      "block text-sm text-brand-navy",
                      !notification.isRead && "font-semibold"
                    )}
                  >
                    {notification.title}
                  </span>
                  <span className="block text-sm text-brand-muted-light break-words">
                    {notification.message}
                  </span>
                  <span className="block mt-1 text-xs text-brand-placeholder">
                    {formatDate(notification.createdDate, locale)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
